import type { AdminPaymentDetail } from '@/lib/api-admin/types';

/**
 * be-payment feat/legacy-manual-refund — 결제 상세의 환불 관련 추가 필드(generated 타입 미반영).
 *
 *   manualRefundOnly      PG 환불 불가(이관 LEGACY / 수기주문) → [수기 환불 기록]만
 *   manualRefundAvailable 수기 환불 기록 가능(위 결제 + Toss 가상계좌는 계좌이체만)
 *   sourcePgProvider      원 PG 표기(LEGACY, MANUAL, TOSSPAYMENTS, KAKAOPAY …)
 *   legacyPg              고도몰 원 PG 정보(이관 결제만, 그 외 null)
 */
export interface LegacyPgInfo {
  /** "kcp" / "kakaopay" / "mobilians" / "allpay" / "payletter" … */
  pgName?: string | null;
  /** "KCP" / "카카오페이" / "모빌리언스(휴대폰)" … */
  pgLabel?: string | null;
  settleKind?: string | null;
  /** "신용카드" / "가상계좌" / "카카오페이" / "VISA/Master/JCB" / "PayPal" … */
  settleKindLabel?: string | null;
  /** PG 거래 번호(카카오페이는 null). */
  pgTid?: string | null;
  /** PG 승인 번호(카카오페이는 TID). */
  pgAppNo?: string | null;
  /** 승인 일시 — KST 원값 문자열. 시간대 변환 없이 그대로 표시한다. */
  pgAppDt?: string | null;
  /** 고도몰에서 이미 PG 취소된 결제. */
  pgCancelled?: boolean | null;
}

type PaymentWithRefundFlags = AdminPaymentDetail & {
  manualRefundOnly?: boolean | null;
  manualRefundAvailable?: boolean | null;
  sourcePgProvider?: string | null;
  legacyPg?: LegacyPgInfo | null;
};

export interface RefundFlags {
  manualOnly: boolean;
  manualAvailable: boolean;
  isLegacy: boolean;
  legacyPg: LegacyPgInfo | null;
  /** 고도몰에서 이미 PG 취소됨 — 수기 환불을 막는다(중복 환불 방지). */
  pgCancelled: boolean;
}

export const refundFlagsOf = (payment: AdminPaymentDetail): RefundFlags => {
  const p = payment as PaymentWithRefundFlags;
  const manualOnly = p.manualRefundOnly === true;
  const legacyPg = p.legacyPg ?? null;
  return {
    manualOnly,
    manualAvailable: manualOnly || p.manualRefundAvailable === true,
    isLegacy: p.sourcePgProvider === 'LEGACY',
    legacyPg,
    pgCancelled: legacyPg?.pgCancelled === true,
  };
};

/**
 * QA-438 — 현금성 결제(계좌이체·가상계좌·무통장입금·수기 결제). 카드 승인 취소(void) 대상이 아니다 — 버튼을 숨긴다.
 */
export const CASH_PAYMENT_METHODS: ReadonlySet<string> = new Set(['TRANSFER', 'VIRTUAL_ACCOUNT', 'BANK_TRANSFER', 'MANUAL_PAID']);
/**
 * QA-438 — 토스로 받은 현금성 결제(계좌이체·가상계좌). 운영자가 토스 관리자에서 수기 환불한 뒤 BO 에는 "환불 상태"만
 * 남겨야 해서 [환불 상태 변경](수기 환불 기록)을 연다. BE 가 이 결제의 수기 기록을 받아야 한다(전달 문서).
 */
export const TOSS_CASH_PAYMENT_METHODS: ReadonlySet<string> = new Set(['TRANSFER', 'VIRTUAL_ACCOUNT']);

/** 원 PG 에서 검색할 번호 — 거래 번호, 없으면 승인 번호(카카오페이는 TID 가 승인 번호 칸에 온다). */
export const legacyPgLookupNo = (legacyPg: LegacyPgInfo | null | undefined): string | null =>
  legacyPg?.pgTid?.trim() || legacyPg?.pgAppNo?.trim() || null;
